import type { FC, FormEvent, ReactNode } from 'react'
import type { BillingCycle, Customer, PlanId } from '@/types'

import { useEffect, useState } from 'react'
import { formatRupiah, formatSignedRupiahCompact } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SegmentedControl } from '@/components/dashboard/segmented-control'
import { FormField, fieldMessageId } from '@/components/dashboard/form-field'
import { PLANS, computeMrr, getPlan } from '@/data/plans'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

interface ChangePlanDialogProps {
    customer: Customer | null
    onOpenChange: (open: boolean) => void
    onSave: (customer: Customer, changes: Pick<Customer, 'plan' | 'cycle' | 'seats' | 'mrr'>) => void
}

const PLAN_ITEMS = PLANS.map((plan) => ({ value: plan.id, label: plan.name }))

export const ChangePlanDialog: FC<ChangePlanDialogProps> = ({ customer, onOpenChange, onSave }): ReactNode => {
    const [plan, setPlan] = useState<PlanId>('growth')
    const [cycle, setCycle] = useState<BillingCycle>('monthly')
    const [seats, setSeats] = useState('1')
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (!customer) return

        setPlan(customer.plan)
        setCycle(customer.cycle)
        setSeats(String(customer.seats))
        setError(null)
    }, [customer])

    const seatCount = Number(seats)
    const validSeats = Number.isInteger(seatCount) && seatCount > 0
    const nextMrr = validSeats ? computeMrr(plan, cycle, seatCount) : null
    const difference = customer && nextMrr !== null ? nextMrr - customer.mrr : 0
    const details = getPlan(plan)

    const onSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()
        if (!customer) return

        if (!validSeats) {
            setError('Jumlah pengguna minimal 1.')
            return
        }

        if (plan === 'starter' && seatCount > 3) {
            setError('Paket Starter maksimal 3 pengguna.')
            return
        }

        onSave(customer, { plan, cycle, seats: seatCount, mrr: computeMrr(plan, cycle, seatCount) })
        onOpenChange(false)
    }

    return (
        <Dialog open={customer !== null} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-md'>
                <form onSubmit={onSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>
                            Ubah paket langganan
                        </DialogTitle>
                        <DialogDescription>
                            {customer ? `${customer.company} saat ini memakai paket ${getPlan(customer.plan).name} dengan ${customer.seats} pengguna.` : ''}
                        </DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4'>
                        <FormField id='change-plan-plan' label='Paket baru'>
                            <Select
                                items={PLAN_ITEMS}
                                value={plan}
                                onValueChange={(value) => {
                                    if (value) {
                                        setPlan(value as PlanId)
                                        setError(null)
                                    }
                                }}
                            >
                                <SelectTrigger id='change-plan-plan' className='w-full'>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {PLANS.map((item) => (
                                        <SelectItem key={item.id} value={item.id}>
                                            <span className='size-2 rounded-full' style={{ background: item.color }} aria-hidden='true' />
                                            {item.name}
                                            <span className='text-xs text-muted-foreground'>{formatRupiah(item.price)}</span>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </FormField>

                        <div className='grid gap-4 sm:grid-cols-2'>
                            <FormField
                                id='change-plan-seats'
                                label='Jumlah pengguna'
                                error={error ?? undefined}
                                hint={`Termasuk ${details.includedSeats} pengguna`}
                            >
                                <Input
                                    id='change-plan-seats'
                                    type='number'
                                    inputMode='numeric'
                                    min={1}
                                    value={seats}
                                    onChange={(event) => {
                                        setSeats(event.target.value)
                                        setError(null)
                                    }}
                                    aria-invalid={Boolean(error)}
                                    aria-describedby={fieldMessageId('change-plan-seats')}
                                />
                            </FormField>

                            <div className='space-y-1.5'>
                                <span className='text-sm leading-none font-medium'>
                                    Siklus tagihan
                                </span>
                                <SegmentedControl
                                    label='Siklus tagihan'
                                    value={cycle}
                                    onChange={setCycle}
                                    options={[
                                        { value: 'monthly', label: 'Bulanan' },
                                        { value: 'annual', label: 'Tahunan' }
                                    ]}
                                    className='flex w-full [&>button]:flex-1'
                                />
                            </div>
                        </div>

                        <dl className='grid grid-cols-2 gap-3 rounded-lg border bg-muted/40 p-3 text-sm'>
                            <div>
                                <dt className='text-xs text-muted-foreground'>MRR baru</dt>
                                <dd className='mt-0.5 font-semibold text-foreground tabular-nums'>
                                    {nextMrr === null ? 'Belum lengkap' : formatRupiah(nextMrr)}
                                </dd>
                            </div>
                            <div>
                                <dt className='text-xs text-muted-foreground'>Perubahan</dt>
                                <dd className='mt-0.5 font-semibold text-foreground tabular-nums'>
                                    {nextMrr === null ? 'Belum lengkap' : formatSignedRupiahCompact(difference)}
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Batal
                        </DialogClose>
                        <Button type='submit'>
                            Simpan perubahan
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}
